/**
 * SelectionNode.ts
 *
 * This custom-extension defines the SelectionNode extension for the TipTap editor.
 * It allows wrapping selected nodes with a custom node that includes a comment ID.
 */

import { mergeAttributes, Node } from '@tiptap/core';
import { COMMENTS_HTML_ATTRIBUTES, SELECTION_NODE_NAME } from '../../constants';

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    'selection-node': {
      wrapSelectionNode: (commentId: string) => ReturnType;
    };
  }
}

/**
 * Augment the global 'WindowEventMap' so that
 * window.addEventListener("selectionNodeRemoved", ...) is typed correctly.
 */
declare global {
  interface WindowEventMap {
    selectionNodeRemoved: CustomEvent<{ commentId: string; isStale: boolean }>;
  }
}

const SelectionNode = Node.create({
  name: SELECTION_NODE_NAME,

  content: 'block+',
  // 'commentSelection' lets block template fields permit this wrapper - their content lists nodes by
  // name, and a hyphenated node name ('selection-node') can't appear in a ProseMirror content expression.
  group: 'block commentSelection',
  defining: true,
  isolating: true, // Prevents the node from being split by the backspace key

  addOptions() {
    return {
      HTMLAttributes: {},
    };
  },

  addAttributes() {
    return {
      commentId: {
        default: null,
        parseHTML: (element) => element.getAttribute(COMMENTS_HTML_ATTRIBUTES.COMMENT_ID),
        renderHTML: (attributes) => ({ [COMMENTS_HTML_ATTRIBUTES.COMMENT_ID]: attributes.commentId }),
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'selection-node',
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    // Not used when a custom NodeView is provided.
    return ['selection-node', mergeAttributes(this.options.HTMLAttributes, HTMLAttributes), 0];
  },

  addCommands() {
    return {
      wrapSelectionNode:
        (commentId) =>
        ({ commands }) => {
          return commands.wrapIn(this.name, { commentId });
        },
    };
  },

  // Custom NodeView that wraps the inner content with a <selection-node> element.
  addNodeView() {
    return ({ node }) => {
      // Create the wrapper element (<selection-node>).
      const wrapper = document.createElement('selection-node');
      // Set the comment ID as a data attribute for styling.
      wrapper.setAttribute(COMMENTS_HTML_ATTRIBUTES.COMMENT_ID, node.attrs.commentId);

      // Create a dedicated content container where TipTap will render the inner node content.
      const contentDOM = document.createElement('div');
      contentDOM.setAttribute('data-node-view-content', '');
      wrapper.appendChild(contentDOM);

      /**
       * The `isStale` flag is used to determine whether the html element should be
       * - kept for content history undo/redo or
       * - removed completely when the comment is deleted (archived).
       *
       * See `handleSelectionNodeRemoved` in `EditorCommentsContainer.tsx` for more details.
       */
      let isStale = true;

      return {
        dom: wrapper,
        contentDOM,
        // The update method says “Yes, I can keep reusing this same NodeView DOM” rather than forcing Tiptap to tear it down each time the node changes.
        update(updatedNode) {
          isStale = updatedNode.attrs.commentId !== undefined; // `commentId` is undefined when the comment is deleted (archived).
          return updatedNode.type === node.type && updatedNode.attrs.commentId === node.attrs.commentId;
        },
        destroy() {
          // Dispatch a custom event when the node is removed.
          const event = new CustomEvent('selectionNodeRemoved', {
            detail: { commentId: node.attrs.commentId, isStale },
          });
          window.dispatchEvent(event);
        },
      };
    };
  },
});

export default SelectionNode;
